import { useQuery } from '@tanstack/react-query';
import { approvalMatrixHealthApi } from '../../api/admin/roles';
import { DataTable } from '../../components/DataTable';

/**
 * Surfaces a real, confirmed gap: resolveApprover() assigns an
 * approver by role name + user_approval_limits alone - entirely
 * independent of whether that role was ever granted the RBAC
 * permissions needed to actually view or decide on what it's
 * assigned to approve. This page cross-checks every configured
 * Approval Matrix rule against real role_permissions and
 * user_approval_limits data, so a broken combination is caught here
 * rather than discovered only when a real request gets stuck.
 */
export function ApprovalMatrixHealthPage() {
  const healthQuery = useQuery({ queryKey: ['approval-matrix-health'], queryFn: () => approvalMatrixHealthApi.check() });
  const data = healthQuery.data?.data;

  return (
    <div className="page">
      <div className="page-header"><h1>Approval Matrix Health</h1></div>
      <p className="muted">Checks every role configured as an approver in the Approval Matrix against what that role is actually granted - a role can be correctly resolved as the approver for a request and still be unable to view or decide on it if these were never cross-checked.</p>

      {data && data.issues.length > 0 && (
        <section className="card">
          <h2>Issues Found ({data.issues.length})</h2>
          <DataTable
            rows={data.issues}
            rowKey={(i) => `${i.module}-${i.approverRole}`}
            emptyMessage=""
            columns={[
              { header: 'Module', render: (i) => i.module },
              { header: 'Approver Role', render: (i) => i.approverRole },
              { header: 'Condition', render: (i) => `${i.conditionType} ${i.conditionValue}` },
              { header: 'Users Holding Role', render: (i) => i.usersHoldingRole },
              { header: 'Missing', render: (i) => <ul style={{ margin: 0, paddingLeft: '1.1em' }}>{i.missingPermissions.map((m) => <li key={m}>{m}</li>)}</ul> },
            ]}
          />
        </section>
      )}

      {data && data.issues.length === 0 && (
        <section className="card"><p>No issues found - every configured approver role holds the permissions it needs.</p></section>
      )}

      {data && data.healthy.length > 0 && (
        <section className="card">
          <h2>Healthy Rules ({data.healthy.length})</h2>
          <DataTable
            rows={data.healthy}
            rowKey={(i) => `${i.module}-${i.approverRole}`}
            emptyMessage=""
            columns={[
              { header: 'Module', render: (i) => i.module },
              { header: 'Approver Role', render: (i) => i.approverRole },
              { header: 'Condition', render: (i) => `${i.conditionType} ${i.conditionValue}` },
            ]}
          />
        </section>
      )}
    </div>
  );
}
